import {
    BaseQueryFn,
    createApi,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    retry,
} from "@reduxjs/toolkit/query/react";
import { languages } from "../../i18n/i18n.constants";
import { Mutex } from "async-mutex";
import { logout, selectLoggedOut } from "../../pages/layoutes/authLayout/authSlice";
import { CurrentSessionResponse } from "../models/api/auth/currentSessionResponse";
import { RootState } from "../store";export type TBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>;

const baseQuery = fetchBaseQuery({
    baseUrl: "/",
    credentials: "include",
    prepareHeaders: (headers) => {
        const lang = localStorage.getItem("i18nextLng") ?? languages.en;
        headers.set("Accept-Language", lang);
        return headers;
    },
});

const staggeredBaseQueryWithBailOut = (baseQuery: TBaseQuery) =>
    retry(
        async (args: string | FetchArgs, api, extraOptions) => {
            const result = await baseQuery(args, api, extraOptions);
            const status = result.error?.status;
            // Bail out (no retry) on client errors and 429 — retrying a rate-limited
            // response only burns the remaining budget faster.
            if (typeof status === "number" && !isNaN(status) && [400, 401, 403, 404, 409, 429].includes(status)) {
                retry.fail(result.error);
            }
            return result;
        },
        {
            maxRetries: Number(import.meta.env.VITE_MAX_RETRIES_REQUEST) || 3,
        }
    );

const mutex = new Mutex();
const baseQueryWithRefreshToken: (baseQuery: TBaseQuery) => TBaseQuery =
    (baseQuery: TBaseQuery) => async (args, api, extraOptions) => {
        const requestTokenRefresh = async (): Promise<boolean> => {
            const refreshResult = await baseQuery(
                {
                    url: `${import.meta.env.VITE_REFRESH_TOKEN}`,
                    method: "POST",
                },
                api,
                extraOptions
            );

            if (!refreshResult.error) {
                // /token/refresh returns the freshly built session payload — seed it directly
                // into the cache instead of invalidating + refetching to avoid a wasted GET.
                // Lazy-import sessionApi to avoid a module init cycle (sessionApi imports baseApi).
                if (refreshResult.data) {
                    const { sessionApi } = await import("./sessionApi");
                    api.dispatch(
                        sessionApi.util.upsertQueryData(
                            "getCurrentSession",
                            undefined,
                            refreshResult.data as CurrentSessionResponse
                        )
                    );
                }
                return true;
            }

            // Refresh failed — sign the user out. Do NOT call resetApiState here: it would
            // pull the cache out from under still-mounted components and trigger them to
            // refetch (→ 401 → another refresh attempt). The `loggedOut` flag in useAuth
            // makes the navbar/PrivateOutlet treat the session as gone immediately; stale
            // per-user data is wiped at the start of the next successful login instead.
            api.dispatch(logout());
            return false;
        };

        const refreshAccessToken = async (): Promise<boolean> => {
            const release = await mutex.acquire();
            try {
                // Re-check inside the lock: another caller may have already refreshed
                // (success → re-issuing the original request will work without a second
                // POST to /token/refresh) or already failed (logged us out → don't pile
                // more requests onto the rate-limited token bucket).
                const state = api.getState() as RootState;
                if (selectLoggedOut(state)) {
                    return false;
                }
                return await requestTokenRefresh();
            } finally {
                release();
            }
        };

        // wait until the mutex is available without locking it
        await mutex.waitForUnlock();

        let result = await baseQuery(args, api, extraOptions);

        if (result.error?.status === 401 && shouldAttemptRefresh(args)) {
            // Don't even try to refresh once the user has logged out — the cookie is gone,
            // /token/refresh would 401, fail back into logout(), and burn rate-limit budget.
            // After waiting for the mutex, a previous holder may also have just refreshed
            // successfully — the re-checked flag covers the auto-logout-on-failure case.
            if (!selectLoggedOut(api.getState() as RootState)) {
                const refreshedBy401 = await refreshAccessToken();
                if (refreshedBy401) {
                    result = await baseQuery(args, api, extraOptions);
                }
            }
        }

        return result;
    };

// Skip the refresh-on-401 dance for auth endpoints themselves to avoid loops
// (e.g. logout returning 401 would otherwise trigger refresh → upsert session → re-fire).
const REFRESH_EXCLUDED_URLS = [
    import.meta.env.VITE_TOKEN,
    import.meta.env.VITE_REFRESH_TOKEN,
    `${import.meta.env.VITE_HOSTNAME_API}/token/logout`,
];

const shouldAttemptRefresh = (args: string | FetchArgs) => {
    const url = typeof args === "string" ? args : args.url;
    const path = url.split("?")[0];
    return !REFRESH_EXCLUDED_URLS.includes(path);
};

export const baseApi = createApi({
    reducerPath: "baseApi",
    baseQuery: baseQueryWithRefreshToken(staggeredBaseQueryWithBailOut(baseQuery)),
    tagTypes: [
        "company",
        "addressBook",
        "orders",
        "userProfile",
        "documents",
        "invoices",
        "exchangeRates",
        "exchangeRatesList",
        "notifications",
        "notificationsUnread",
        "changeRequests",
        "proposals",
        "managementOrders",
        "session",
        "tariffTypeSurcharges",
        "selectedTariffSurcharges",
        "tariffSurcharges",
        "directions",
        "users",
    ],
    endpoints: () => ({}),
});
